"use client";

import { forwardRef, useState, useEffect, useRef } from "react";
import { DollarSign } from "lucide-react";
import { type FieldError } from "react-hook-form";

import { formatCurrency, parseCurrencyInput, formatCurrencyInput } from "@/app/_module/utils/currencyUtils";
import Input from "../FormInput";

interface CurrencyInputProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: FieldError;
  value: string;
  onChange: (value: string) => void;
  containerClass?: string;
  subText?: string;
  disabled?: boolean;
  id?: string;
  showIcon?: boolean;
  maxAmount?: number;
  currency?: string;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({
    onChange,
    placeholder = "$50,000",
    subText,
    value,
    showIcon = true,
    maxAmount,
    currency = "USD",
    ...props
  }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Update display value when value prop changes (only when not focused to avoid cursor issues)
    useEffect(() => {
      if (!isFocused) {
        if (value) {
          const numericValue = parseCurrencyInput(value);
          setDisplayValue(numericValue > 0 ? formatCurrencyInput(numericValue) : "");
        } else {
          setDisplayValue("");
        }
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const cursorPosition = e.target.selectionStart || 0;

      // Remove any non-digit characters except decimal point
      const cleaned = inputValue.replace(/[^\d.]/g, "");

      // Ensure only one decimal point
      const parts = cleaned.split(".");
      const sanitized = parts.length > 2
        ? parts[0] + "." + parts.slice(1).join("")
        : cleaned;

      // Apply max amount limit if specified
      if (maxAmount) {
        const numericValue = parseFloat(sanitized);
        if (numericValue > maxAmount) {
          return; // Don't update if exceeds max
        }
      }

      // Format with commas in real-time while typing
      const numericValue = parseFloat(sanitized);
      if (!isNaN(numericValue) && numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue);

        // Calculate new cursor position after formatting
        const oldCommaCount = (displayValue.match(/,/g) || []).length;
        const newCommaCount = (formatted.match(/,/g) || []).length;
        const commasDiff = newCommaCount - oldCommaCount;

        setDisplayValue(formatted);

        // Restore cursor position accounting for added/removed commas
        setTimeout(() => {
          if (inputRef.current) {
            const newPosition = cursorPosition + commasDiff;
            inputRef.current.setSelectionRange(newPosition, newPosition);
          }
        }, 0);
      } else {
        setDisplayValue(sanitized);
      }

      // Store the raw numeric value (without formatting)
      onChange(isNaN(numericValue) ? "" : sanitized);
    };

    const handleFocus = () => {
      setIsFocused(true);
      // Keep formatted display even when focused for real-time formatting
      const numericValue = parseCurrencyInput(value);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue);
        setDisplayValue(formatted);
      } else {
        setDisplayValue("");
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Ensure formatting is applied on blur
      const numericValue = parseCurrencyInput(displayValue || value);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue);
        setDisplayValue(formatted);
        // Update the parent with the raw numeric value as string
        onChange(numericValue.toString());
      } else {
        setDisplayValue("");
        onChange("");
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right arrows
      if (
        [
          "Backspace",
          "Tab", 
          "Escape",
          "Enter",
          "Delete",
          "Home",
          "End",
          "ArrowLeft",
          "ArrowRight",
        ].includes(e.key) ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.key === "a" && e.ctrlKey) ||
        (e.key === "c" && e.ctrlKey) ||
        (e.key === "v" && e.ctrlKey) ||
        (e.key === "x" && e.ctrlKey)
      ) {
        return;
      }

      // Allow digits and decimal point
      if (
        (e.key >= "0" && e.key <= "9") ||
        (e.key >= "Numpad0" && e.key <= "Numpad9") ||
        (e.key === "." && !displayValue.includes("."))
      ) {
        return;
      }

      e.preventDefault();
    };

    return (
      <div className={props.containerClass}>
        <div className="relative">
          {showIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <DollarSign className="h-4 w-4" />
            </div>
          )}
          <Input
            type="text"
            placeholder={placeholder}
            inputMode="decimal"
            {...props}
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            ref={(element) => {
              // Handle both refs (forwarded ref and internal ref)
              inputRef.current = element;
              if (typeof ref === 'function') {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
            }}
            className={`${showIcon ? "pl-10" : ""} ${props.disabled ? "opacity-60" : ""}`}
          />
        </div>
        {subText && (
          <p className="text-xs text-muted-foreground mt-1">{subText}</p>
        )}
        {maxAmount && (
          <p className="text-xs text-gray-500 mt-1">
            Maximum: {formatCurrency(maxAmount, currency)}
          </p>
        )}
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };